import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../components/Modal.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function AdminDashboard({ user, onNavigate }) {
  console.log('AdminDashboard component rendered with user:', user);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [passwordResetModal, setPasswordResetModal] = useState({ show: false, userId: null, username: '' });
  const [resetPassword, setResetPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');
  const [showClosedTickets, setShowClosedTickets] = useState(false);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('AdminDashboard: Starting to fetch data from', `${API_BASE}/admin/users`, `${API_BASE}/admin/tickets`);
      const [usersRes, ticketsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/users`).catch(err => {
          console.error('Error fetching users:', err);
          throw new Error(`Failed to fetch users: ${err.response?.data?.error || err.message}`);
        }),
        axios.get(`${API_BASE}/admin/tickets`).catch(err => {
          console.error('Error fetching tickets:', err);
          throw new Error(`Failed to fetch tickets: ${err.response?.data?.error || err.message}`);
        })
      ]);
      console.log('AdminDashboard: Data fetched successfully', { users: usersRes.data, tickets: ticketsRes.data });
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.message || error.response?.data?.error || 'Unknown error';
      setError(errorMessage);
      setUsers([]);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      console.log('AdminDashboard: User loaded, fetching data...', user);
      fetchAllData();
    } else {
      console.log('AdminDashboard: No user, cannot fetch data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_BASE}/admin/users/${userId}/role`, { role: newRole });
      await fetchAllData();
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Error updating user role: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleDisabled = async (userId, currentStatus) => {
    const action = currentStatus ? 'enable' : 'disable';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await axios.put(`${API_BASE}/admin/users/${userId}/disabled`, { disabled: !currentStatus });
      await fetchAllData();
    } catch (error) {
      console.error('Error updating user disabled status:', error);
      alert('Error updating user status: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleTicketStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API_BASE}/admin/tickets/${ticketId}/status`, { status: newStatus });
      await fetchAllData();
    } catch (error) {
      console.error('Error updating ticket status:', error);
      alert('Error updating ticket status: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleTicketResponse = async (ticketId) => {
    if (!ticketResponse.trim()) {
      alert('Please enter a response');
      return;
    }

    try {
      await axios.put(`${API_BASE}/admin/tickets/${ticketId}/response`, { response: ticketResponse });
      await fetchAllData();
      // Update selected ticket with new response
      const updatedTickets = await axios.get(`${API_BASE}/admin/tickets`);
      const updatedTicket = updatedTickets.data.find(t => t.id === ticketId);
      if (updatedTicket) {
        setSelectedTicket(updatedTicket);
      }
      setTicketResponse('');
      alert('Response sent successfully!');
    } catch (error) {
      console.error('Error sending ticket response:', error);
      alert('Error sending response: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAdminCloseTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }

    try {
      await axios.put(`${API_BASE}/admin/tickets/${ticketId}/close`);
      await fetchAllData();
      setSelectedTicket(null);
      alert('Ticket closed successfully!');
    } catch (error) {
      console.error('Error closing ticket:', error);
      alert('Error closing ticket: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/admin/tickets/${ticketId}`);
      await fetchAllData();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket(null);
      }
      alert('Ticket deleted successfully!');
    } catch (error) {
      console.error('Error deleting ticket:', error);
      alert('Error deleting ticket: ' + (error.response?.data?.error || error.message));
    }
  };

  const generateRandomPassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setGeneratedPassword(password);
    setResetPassword(password);
    return password;
  };

  const openPasswordResetModal = (userId, username) => {
    setPasswordResetModal({ show: true, userId, username });
    setResetPassword('');
    setGeneratedPassword('');
  };

  const closePasswordResetModal = () => {
    setPasswordResetModal({ show: false, userId: null, username: '' });
    setResetPassword('');
    setGeneratedPassword('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Password copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Password copied to clipboard!');
    });
  };

  const handleResetPassword = async () => {
    if (!resetPassword) {
      alert('Please enter a password or generate one');
      return;
    }

    if (resetPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    try {
      await axios.put(`${API_BASE}/admin/users/${passwordResetModal.userId}/password`, { 
        newPassword: resetPassword 
      });
      alert(`Password reset successfully for ${passwordResetModal.username}!`);
      closePasswordResetModal();
      await fetchAllData();
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This will also delete all their data (gigs, clients, locations).')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/admin/users/${userId}`);
      await fetchAllData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-screen">
          <p>Loading admin data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">Manage users and view all application data</p>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#ff6b6b', maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>Error loading admin data</h2>
          <div style={{ 
            background: '#2d2d2d', 
            padding: '1.5rem', 
            borderRadius: '8px', 
            marginBottom: '1.5rem',
            border: '1px solid #444',
            textAlign: 'left'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Error Details:</p>
            <p style={{ margin: 0, wordBreak: 'break-word' }}>{error}</p>
          </div>
          <p style={{ color: '#999', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Please check the browser console (F12) for more details.
          </p>
          <button 
            className="btn btn-primary" 
            onClick={fetchAllData}
            style={{ marginTop: '1rem' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  console.log('AdminDashboard rendering with state:', { loading, error, usersCount: users.length, ticketsCount: tickets.length });

  return (
    <div className="admin-dashboard">
      <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">Manage users and view all application data</p>
        </div>
        {onNavigate && (
          <button 
            className="btn btn-secondary"
            onClick={() => onNavigate('dashboard')}
            style={{ marginLeft: '1rem' }}
          >
            ← Back to Dashboard
          </button>
        )}
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('users');
            setSearchQuery('');
          }}
        >
          👥 Users ({users.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'tickets' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('tickets');
            setSearchQuery('');
          }}
        >
          🎫 Tickets ({tickets.length})
        </button>
      </div>

      <div className="admin-content">
        <div className="admin-search-container">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="admin-search-input"
          />
          {searchQuery && (
            <button
              className="admin-search-clear"
              onClick={() => setSearchQuery('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {activeTab === 'users' && (
          <div className="admin-section">
            <h2>User Management</h2>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                        No users found.
                      </td>
                    </tr>
                  ) : (
                  users
                    .filter(u => {
                      if (!searchQuery) return true;
                      const query = searchQuery.toLowerCase();
                      return (
                        u.id.toString().includes(query) ||
                        u.username.toLowerCase().includes(query) ||
                        u.email.toLowerCase().includes(query) ||
                        (u.role && u.role.toLowerCase().includes(query))
                      );
                    })
                    .map((u) => {
                      const isDisabled = u.disabled === 1 || u.disabled === true;
                      return (
                      <tr key={u.id} style={isDisabled ? { opacity: 0.6 } : {}}>
                        <td>{u.id}</td>
                        <td>
                          {u.username}
                          {isDisabled && <span style={{ color: '#ff6b6b', marginLeft: '0.5rem', fontSize: '0.85rem' }}>(Disabled)</span>}
                        </td>
                        <td>{u.email}</td>
                        <td>
                          <select
                            value={u.role || 'user'}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className="role-select"
                            disabled={isDisabled}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="btn"
                            onClick={() => handleToggleDisabled(u.id, isDisabled)}
                            style={{
                              padding: '0.4rem 0.8rem',
                              fontSize: '0.85rem',
                              background: isDisabled ? '#4caf50' : '#ff6b6b',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            title={isDisabled ? 'Enable user' : 'Disable user'}
                          >
                            {isDisabled ? '✓ Enable' : '✕ Disable'}
                          </button>
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button
                              className="btn-icon"
                              onClick={() => openPasswordResetModal(u.id, u.username)}
                              title="Reset password"
                              style={{ color: '#ffd700' }}
                            >
                              Reset Password
                            </button>
                            {u.id !== user.id && (
                              <button
                                className="btn-icon"
                                onClick={() => handleDeleteUser(u.id)}
                                title="Delete user"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'tickets' && (() => {
          const openTickets = tickets.filter(t => t.status === 'open');
          const inProgressTickets = tickets.filter(t => t.status === 'in_progress');
          const closedTickets = tickets.filter(t => t.status === 'closed');

          const filterTickets = (ticketList) => {
            if (!searchQuery) return ticketList;
            const query = searchQuery.toLowerCase();
            return ticketList.filter(ticket => 
              ticket.id.toString().includes(query) ||
              (ticket.subject && ticket.subject.toLowerCase().includes(query)) ||
              (ticket.user_username && ticket.user_username.toLowerCase().includes(query)) ||
              (ticket.status && ticket.status.toLowerCase().includes(query)) ||
              (ticket.priority && ticket.priority.toLowerCase().includes(query))
            );
          };

          const renderTicketTable = (ticketList, title) => (
            <div className="admin-table-container">
              {title && <h3 style={{ color: '#ffd700', marginBottom: '1rem', fontSize: '1.2rem' }}>{title} ({ticketList.length})</h3>}
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Subject</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterTickets(ticketList).map((ticket) => (
                    <tr key={ticket.id}>
                      <td>{ticket.id}</td>
                      <td>{ticket.user_username || 'Unknown'}</td>
                      <td>{ticket.subject}</td>
                      <td>
                        <span 
                          className="ticket-badge"
                          style={{
                            backgroundColor: ticket.priority === 'high' ? '#ff6b6b' : ticket.priority === 'medium' ? '#ffd700' : '#4caf50',
                            color: '#1a1a1a',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}
                        >
                          {ticket.priority}
                        </span>
                      </td>
                      <td>
                        <select
                          value={ticket.status || 'open'}
                          onChange={(e) => handleTicketStatusChange(ticket.id, e.target.value)}
                          className="role-select"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setTicketResponse('');
                          }}
                          title="View/Respond"
                          style={{ color: '#4a9eff' }}
                        >
                          View
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteTicket(ticket.id)}
                          title="Delete Ticket"
                          style={{ color: '#ff6b6b', marginLeft: '0.5rem' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

          return (
            <div className="admin-section">
              <h2>All Tickets</h2>
              
              {/* Open Tickets */}
              {openTickets.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  {renderTicketTable(openTickets, 'Open Tickets')}
                </div>
              )}

              {/* In Progress Tickets */}
              {inProgressTickets.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  {renderTicketTable(inProgressTickets, 'In Progress (WIP)')}
                </div>
              )}

              {/* Closed Tickets - Collapsible */}
              {closedTickets.length > 0 && (
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #444' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <h3 style={{ color: '#999', fontSize: '1.1rem', margin: 0 }}>Closed Tickets ({closedTickets.length})</h3>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowClosedTickets(!showClosedTickets)}
                      style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
                    >
                      {showClosedTickets ? '▼ Hide' : '▶ Show'} Closed
                    </button>
                  </div>
                  {showClosedTickets && (
                    <div style={{ opacity: 0.7 }}>
                      {renderTicketTable(closedTickets, '')}
                    </div>
                  )}
                </div>
              )}

              {openTickets.length === 0 && inProgressTickets.length === 0 && closedTickets.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                  No tickets found.
                </div>
              )}
            </div>
          );
        })()}

      </div>

      {/* Ticket Response Modal */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ticket #{selectedTicket.id}: {selectedTicket.subject}</h2>
              <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="ticket-details">
                <div className="ticket-section">
                  <h4>From: {selectedTicket.user_username} ({selectedTicket.user_email})</h4>
                  <p className="ticket-message" style={{ marginTop: '1rem', whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.message}
                  </p>
                  <small className="ticket-meta">
                    Created: {new Date(selectedTicket.created_at).toLocaleString()}
                  </small>
                </div>

                {selectedTicket.admin_response && (
                  <div className="ticket-section ticket-admin-response" style={{ marginTop: '1.5rem' }}>
                    <h4>Previous Admin Response(s)</h4>
                    <p className="ticket-message" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTicket.admin_response}
                    </p>
                    <small className="ticket-meta">
                      Last updated: {new Date(selectedTicket.updated_at).toLocaleString()}
                    </small>
                  </div>
                )}

                <div className="form-group" style={{ marginTop: '2rem' }}>
                  <label htmlFor="ticketResponse">Admin Response</label>
                  <textarea
                    id="ticketResponse"
                    value={ticketResponse}
                    onChange={(e) => setTicketResponse(e.target.value)}
                    className="form-input form-textarea"
                    rows="6"
                    placeholder="Enter your response here..."
                  />
                  <small style={{ color: '#999', fontSize: '0.85rem' }}>
                    You can add multiple responses. Responses will be appended to existing ones.
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setSelectedTicket(null);
                  setTicketResponse('');
                }}
              >
                Close
              </button>
              {selectedTicket.status !== 'closed' && (
                <>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => handleAdminCloseTicket(selectedTicket.id)}
                    style={{ background: '#ff6b6b' }}
                  >
                    Close Ticket
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => handleTicketResponse(selectedTicket.id)}
                  >
                    Send Response
                  </button>
                </>
              )}
              {selectedTicket.status === 'closed' && ticketResponse && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleTicketResponse(selectedTicket.id)}
                >
                  Send Response
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordResetModal.show && (
        <div className="modal-overlay" onClick={closePasswordResetModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password for {passwordResetModal.username}</h2>
              <button className="modal-close" onClick={closePasswordResetModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="resetPassword">New Password</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    id="resetPassword"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter password or generate one"
                    style={{ flex: 1 }}
                  />
                  {generatedPassword && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => copyToClipboard(generatedPassword)}
                      title="Copy password"
                    >
                      📋
                    </button>
                  )}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={generateRandomPassword}
                    style={{ fontSize: '0.9rem' }}
                  >
                    🎲 Generate Random Password
                  </button>
                  {generatedPassword && (
                    <span style={{ color: '#999', fontSize: '0.85rem', alignSelf: 'center' }}>
                      Generated: {generatedPassword.length} characters
                    </span>
                  )}
                </div>
                <small className="form-hint">Password must be at least 6 characters long</small>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closePasswordResetModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleResetPassword}>
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

